import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import ProductList from 'components/Content/ProductList';
import ProductTable from 'components/ProductPage/ProductTable';
import BccCarousel from 'components/Bcc/BccCarousel/BccCarousel';
import { wrapFunctionalComponent } from 'utils/framework';
import BCCUtils from 'utils/BCC';
import PropTypes from 'prop-types';
import ProductItem from 'components/Product/ProductItem';
import { CONSTRUCTOR_PODS } from 'constants/constructorConstants';
import { Grid, Text } from 'components/ui';
import { fontSizes, fonts, space } from 'style/config';
import { recommendationsServiceSelector } from 'selectors/recommendationsService/recommendationsServiceSelector';
import RecommendationsServiceActions from 'actions/RecommendationsServiceActions';
import preferredStoreInfoSelector from 'selectors/user/preferredStoreInfoSelector';
import recServiceUtils from 'utils/RecommendationService';

const { COMPONENT_NAMES, IMAGE_SIZES } = BCCUtils;
const { transformRecommendationsForCarousel } = recServiceUtils;

const RecServiceCarousel = props => {
    const {
        params = {},
        podId,
        title = '',
        skuList = [],
        onConstructorFailed = () => {},
        currentProduct,
        showTitle,
        isSmallView,
        isCAMobile = false,
        closeParentModal,
        customStyles,
        showBasketGreyBackground,
        customTitle,
        scrollPadding,
        grouping,
        ...restProps
    } = props;

    // Redux selectors
    const dispatch = useDispatch();
    const { recommendations = {} } = useSelector(recommendationsServiceSelector, shallowEqual);
    const preferredStoreInfo = useSelector(preferredStoreInfoSelector, shallowEqual);
    const preferredStoreId = preferredStoreInfo?.storeId || null;

    // Local state
    const [localRecommendations, setLocalRecommendations] = useState([]);
    const [displayTitle, setDisplayTitle] = useState(title || '');
    const [showSkeleton, setShowSkeleton] = useState(true);
    const [resultId, setResultId] = useState(null);
    const [totalResults, setTotalResults] = useState(null);

    // Helper to build convenience hub params
    const getConvenienceHubParams = useCallback(storeId => {
        return storeId
            ? {
                filters: [{ key: 'availability', value: `store_${storeId}` }]
            }
            : {};
    }, []);

    // Process recommendations from Redux store
    const processRecommendations = recsData => {
        const currentRecs = recsData[podId];

        if (currentRecs?.isError || !currentRecs) {
            // Fallback to provided skuList on error
            setLocalRecommendations(skuList || []);
            setDisplayTitle(title || '');
            onConstructorFailed();
        } else {
            // Transform raw recommendations from GraphQL structure to carousel-expected structure
            const rawRecommendations = currentRecs.recommendations || [];
            const transformedRecommendations = transformRecommendationsForCarousel(rawRecommendations, podId);

            setLocalRecommendations(transformedRecommendations);
            setDisplayTitle(currentRecs.displayName || title || '');
            setResultId(currentRecs.resultId || null);
            setTotalResults(currentRecs.totalNumResults || null);
        }

        if (currentRecs !== undefined) {
            setShowSkeleton(false);
        }
    };

    // Fetch recommendations
    const fetchRecommendations = useCallback(
        async payload => {
            if (!payload) {
                return;
            }

            try {
                setShowSkeleton(true);
                await dispatch(RecommendationsServiceActions.fetchRecommendations(payload));
            } catch (err) {
                // Error handled by reducer, will be in error state
                setShowSkeleton(false);
            }
        },
        [dispatch]
    );

    // Conditional dependency: only track preferredStoreId for convenience hub
    const isConvenienceHub = podId === CONSTRUCTOR_PODS.RFY_CONVENIENCE_HUB;
    const fetchDependency = isConvenienceHub ? preferredStoreId : null;

    // Mount effect - initial data fetch
    useEffect(() => {
        if (isConvenienceHub && !preferredStoreId) {
            return;
        }

        const requestPayload = isConvenienceHub
            ? {
                podId,
                ...getConvenienceHubParams(preferredStoreId)
            }
            : {
                podId,
                ...params
            };

        fetchRecommendations(requestPayload);

        // Process if data already exists (SSR scenario)
        if (recommendations[podId]) {
            processRecommendations(recommendations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchDependency]);

    // Process recommendations when Redux state updates
    useEffect(() => {
        if (recommendations[podId]) {
            processRecommendations(recommendations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, podId]);

    // Render carousel based on podId
    const renderCarousel = () => {
        switch (podId) {
            case CONSTRUCTOR_PODS.SIMILAR_PRODUCTS:
                return isCAMobile ? (
                    <ProductList
                        {...restProps}
                        skuList={localRecommendations}
                        title={displayTitle}
                        grouping={grouping}
                        showSkeleton={showSkeleton}
                        podId={podId}
                        customStyles={customStyles}
                        resultId={resultId}
                        totalResults={totalResults}
                        isCarousel={true}
                    />
                ) : (
                    <ProductTable
                        skuList={localRecommendations}
                        isSmallView={isSmallView}
                        currentProduct={currentProduct}
                        showTitle={showTitle}
                        title={displayTitle}
                        podId={podId}
                        resultId={resultId}
                        totalResults={totalResults}
                        isCarousel={true}
                    />
                );
            case CONSTRUCTOR_PODS.ATB:
            case CONSTRUCTOR_PODS.ORDER_CONFIRMATION:
            case CONSTRUCTOR_PODS.COMMUNITY_PROFILE:
                return (
                    <BccCarousel
                        {...restProps}
                        showArrows={true}
                        showTouts={Sephora.isMobile()}
                        title={displayTitle}
                        mobileWebTitleText={displayTitle}
                        carouselItems={localRecommendations}
                        totalItems={localRecommendations.length}
                        componentType={COMPONENT_NAMES.CAROUSEL}
                        podId={podId}
                        resultId={resultId}
                        totalResults={totalResults}
                        isCarousel={true}
                    />
                );
            case CONSTRUCTOR_PODS.PURCHASE_HISTORY:
            case CONSTRUCTOR_PODS.SIMILAR_PRODUCTS_CONTENT_PAGE:
            case CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT:
                return (
                    <React.Fragment>
                        <Text
                            is='h3'
                            lineHeight='none'
                            css={
                                podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT
                                    ? { fontSize: fontSizes.lg, textAlign: 'left', fontWeight: 'var(--font-weight-bold)', marginBottom: space[4] }
                                    : {
                                        fontSize: fontSizes.xl,
                                        textAlign: 'center',
                                        marginTop: space[3],
                                        fontFamily: fonts.serif,
                                        marginBottom: space[5]
                                    }
                            }
                            children={displayTitle}
                        />
                        <Grid
                            gap={5}
                            columns={[2, 5]}
                            data-cnstrc-recommendations
                            data-cnstrc-recommendations-pod-id={podId}
                            data-cnstrc-result-id={resultId}
                            data-cnstrc-num-results={totalResults}
                        >
                            {localRecommendations.map((item, index) => (
                                <ProductItem
                                    podId={podId}
                                    formatValuePrice={true}
                                    position={index}
                                    key={item.skuId}
                                    imageSize={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT ? 132 : IMAGE_SIZES[135]}
                                    showQuickLook={true}
                                    showPrice={true}
                                    showReviews={true}
                                    showBadges={true}
                                    showMarketingFlags={true}
                                    useAddToBasket={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT}
                                    isAddButton={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT}
                                    closeParentModal={closeParentModal}
                                    isCleanAtSephora={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT}
                                    {...item}
                                />
                            ))}
                        </Grid>
                    </React.Fragment>
                );
            default:
                return (
                    <ProductList
                        {...restProps}
                        skuList={localRecommendations}
                        title={customTitle || displayTitle}
                        grouping={grouping}
                        showSkeleton={showSkeleton}
                        podId={podId}
                        customStyles={customStyles}
                        showBasketGreyBackground={showBasketGreyBackground}
                        resultId={resultId}
                        totalResults={totalResults}
                        scrollPadding={scrollPadding}
                    />
                );
        }
    };

    return showSkeleton || localRecommendations?.length > 0 ? renderCarousel() : null;
};

RecServiceCarousel.propTypes = {
    podId: PropTypes.string.isRequired,
    params: PropTypes.object,
    isCollection: PropTypes.bool,
    title: PropTypes.string,
    skuList: PropTypes.array,
    onConstructorFailed: PropTypes.func,
    currentProduct: PropTypes.object,
    showTitle: PropTypes.bool,
    isSmallView: PropTypes.bool,
    isCAMobile: PropTypes.bool,
    closeParentModal: PropTypes.func,
    customStyles: PropTypes.object,
    showBasketGreyBackground: PropTypes.bool,
    customTitle: PropTypes.string,
    scrollPadding: PropTypes.string,
    grouping: PropTypes.array
};

export default wrapFunctionalComponent(RecServiceCarousel, 'RecServiceCarousel');
